'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "render", "resourceId", "addPropertiesToDroppedEvent", "children"];
import * as React from 'react';
import { useStore } from '@base-ui/utils/store';
import { useRenderElement } from '@mui/x-scheduler-headless/base-ui-copy';
import { schedulerOccurrenceSelectors } from '@mui/x-scheduler-headless/scheduler-selectors';
import { useEventOccurrencesWithTimelinePosition } from '@mui/x-scheduler-headless/use-event-occurrences-with-timeline-position';
import { useAdapterContext } from '@mui/x-scheduler-headless/use-adapter-context';
import { useEventCreation } from '@mui/x-scheduler-headless/internals';
import { EVENT_CREATION_PRECISION_MINUTE } from '@mui/x-scheduler-headless/constants';
import { TimelineGridEventRowContext } from "./TimelineGridEventRowContext.mjs";
import { useEventRowDropTarget } from "./useEventRowDropTarget.mjs";
import { usePlaceholderInRow } from "./usePlaceholderInRow.mjs";
import { useEventTimelinePremiumStoreContext } from "../../use-event-timeline-premium-store-context/index.mjs";
import { eventTimelinePremiumViewSelectors } from "../../event-timeline-premium-selectors/index.mjs";
import { TimelineGridEventRowDataAttributes } from "./TimelineGridEventRowDataAttributes.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
const stateAttributesMapping = {
  resourceId: value => ({
    [TimelineGridEventRowDataAttributes.resourceId]: String(value)
  })
};
export const TimelineGridEventRow = /*#__PURE__*/React.forwardRef(function TimelineGridEventRow(componentProps, forwardedRef) {
  const {
      // Rendering props

      // Internal props
      resourceId,
      addPropertiesToDroppedEvent,
      children: childrenProp
      // Props forwarded to the DOM element
    } = componentProps,
    elementProps = _objectWithoutPropertiesLoose(componentProps, _excluded);

  // Context hooks
  const adapter = useAdapterContext();
  const store = useEventTimelinePremiumStoreContext();

  // Selector hooks
  const viewConfig = useStore(store, eventTimelinePremiumViewSelectors.config);
  const occurrences = useStore(store, schedulerOccurrenceSelectors.resourceOccurrences, viewConfig.start, viewConfig.end, resourceId);

  // Feature hooks
  const {
    getCursorPositionInElementMs,
    ref: dropTargetRef
  } = useEventRowDropTarget({
    resourceId,
    addPropertiesToDroppedEvent
  });
  const eventCreationProps = useEventCreation(({
    event,
    creationConfig
  }) => {
    const offsetMs = getCursorPositionInElementMs({
      input: {
        clientX: event.clientX
      },
      elementRef: dropTargetRef
    });
    const anchor = adapter.addMilliseconds(viewConfig.start, offsetMs);
    const startDate = adapter.addMinutes(anchor, -(adapter.getMinutes(anchor) % EVENT_CREATION_PRECISION_MINUTE));
    return {
      surfaceType: 'timeline',
      start: startDate,
      end: adapter.addMinutes(startDate, creationConfig.duration),
      resourceId,
      lockSurfaceType: true
    };
  });
  const contextValue = React.useMemo(() => ({
    getCursorPositionInElementMs
  }), [getCursorPositionInElementMs]);
  const occurrencesWithPosition = useEventOccurrencesWithTimelinePosition({
    occurrences,
    maxSpan: 1
  });
  const placeholder = usePlaceholderInRow({
    resourceId,
    occurrences: occurrencesWithPosition.occurrences,
    maxIndex: occurrencesWithPosition.maxIndex
  });
  const children = React.useMemo(() => childrenProp(_extends({
    placeholder
  }, occurrencesWithPosition)), [childrenProp, placeholder, occurrencesWithPosition]);
  const state = {
    resourceId
  };

  // TODO: Add aria-rowindex using Composite.
  const props = {
    role: 'row',
    children
  };
  const element = useRenderElement('div', componentProps, {
    ref: [forwardedRef, dropTargetRef],
    state,
    stateAttributesMapping,
    props: [props, eventCreationProps, elementProps]
  });
  return /*#__PURE__*/_jsx(TimelineGridEventRowContext.Provider, {
    value: contextValue,
    children: element
  });
});
if (process.env.NODE_ENV !== "production") TimelineGridEventRow.displayName = "TimelineGridEventRow";